import {
  initEuConsentAndGA,
  initPostHog,
  syncPostHogIdentity,
  type PostHogLike,
} from '@hookdeck/analytics';
import posthog from 'posthog-js';
import { PropsWithChildren, useContext, useEffect, useRef } from 'react';

import { ConsoleGlobalContext } from '../context/ConsoleGlobalContext';

const as_posthog_identity = (instance: typeof posthog): PostHogLike =>
  instance as unknown as PostHogLike;

const TrackingProvider: React.FC<PropsWithChildren<{ telemetry: boolean }>> = ({
  children,
  telemetry,
}) => {
  const { user } = useContext(ConsoleGlobalContext);
  const posthog_initialized = useRef(false);

  useEffect(() => {
    if (!telemetry || posthog_initialized.current || typeof window === 'undefined') {
      return;
    }

    const start_posthog = (opt_out: boolean) => {
      if (posthog_initialized.current) {
        if (opt_out) {
          posthog.opt_out_capturing();
        } else {
          posthog.opt_in_capturing();
        }
        return;
      }

      initPostHog(posthog.init.bind(posthog), {
        api_key: VITE_POSTHOG_API_KEY || '',
        api_host: VITE_POSTHOG_HOST || 'https://app.posthog.com',
        person_profiles: 'identified_only',
        opt_out_capturing_by_default: opt_out,
        debug: VITE_APP_ENV === 'development',
        loaded: (instance) => {
          window.posthog = instance;
          if (user?.id) {
            syncPostHogIdentity(as_posthog_identity(instance), user, { env: VITE_APP_ENV });
          }
          if (opt_out) {
            instance.opt_out_capturing();
          } else {
            instance.opt_in_capturing();
          }
        },
      });
      posthog_initialized.current = true;
    };

    initEuConsentAndGA({
      ga_id: VITE_GA_ID || '',
      on_analytics_accepted: () => start_posthog(false),
      on_analytics_rejected: () => start_posthog(true),
    });
  }, [telemetry]);

  useEffect(() => {
    if (!telemetry || !posthog_initialized.current || !user?.id) {
      return;
    }
    syncPostHogIdentity(as_posthog_identity(posthog), user, { env: VITE_APP_ENV });
  }, [telemetry, user]);

  return <>{children}</>;
};

export default TrackingProvider;
